import { formattedDecimal, formattedPercent, formattedUSD } from '@/utils.ts';
import {
  ArrowDownward,
  ArrowUpward,
  Bolt,
  Paid,
  PropaneTank,
} from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { theme } from '../../theme/theme.ts';
import { Tab } from '../types';

type Props = {
  conventionalCostAndEnergyMetrics?: { cost?: number; energyDemand: number };
  costAndEnergyMetrics?: { cost?: number; energyDemand: number };
  selected: boolean;
  tab: Tab;
  onClick: () => void;
};

export const BaseDecarbTab: React.FC<Props> = React.memo(
  ({
    conventionalCostAndEnergyMetrics,
    costAndEnergyMetrics,
    selected,
    tab,
    onClick,
  }) => {
    // cost/energyDemand may be undefined if a selection hasn't been made for this tab
    const { cost, energyDemand } = costAndEnergyMetrics || {};
    // conventional cost/energyDemand may be undefined if currenttab === conventional
    // or a selection hasn't been made
    const { cost: conventionalCost, energyDemand: conventionalEnergyDemand } =
      conventionalCostAndEnergyMetrics || {};

    let costCompareIcon;
    if (cost !== undefined && conventionalCost !== undefined) {
      if (cost - conventionalCost > 0) {
        costCompareIcon = <ArrowUpward />;
      } else {
        costCompareIcon = <ArrowDownward />;
      }
    }

    let energyCompareIcon;
    if (energyDemand !== undefined && conventionalEnergyDemand !== undefined) {
      if (energyDemand - conventionalEnergyDemand > 0) {
        energyCompareIcon = <ArrowUpward />;
      } else {
        energyCompareIcon = <ArrowDownward />;
      }
    }

    const costComparedToConventional =
      cost !== undefined && conventionalCost !== undefined
        ? getPercentage(cost, conventionalCost)
        : undefined;

    const energyComparedToConventional =
      energyDemand !== undefined && conventionalEnergyDemand !== undefined
        ? getPercentage(energyDemand, conventionalEnergyDemand)
        : undefined;

    function getPercentage(numerator: number, denominator: number): string {
      if (denominator === 0 && numerator !== 0) {
        return formattedPercent(1);
      }
      if (denominator === 0 && numerator === 0) {
        return formattedPercent(0);
      }
      if (numerator > denominator) {
        return getPercentage(denominator, numerator);
      }

      return formattedPercent(1 - numerator / denominator);
    }

    let title = '';

    if (tab === 'conventionalAlternative') {
      title = 'CONVENTIONAL';
    } else if (tab === 'reducedCarbonAlternative') {
      title = 'LOW CARBON VS CONVENTIONAL';
    } else {
      title = 'NET ZERO VS CONVENTIONAL';
    }

    return (
      <Box flex={'1 1 0'} onClick={onClick} sx={{ cursor: 'pointer' }}>
        {/* flex grow: 1, flex shrink: 1, flex basis: 0, shorthand for:
      make each tab take up 1/3 of space each*/}
        <Paper
          elevation={0}
          sx={{
            height: '100%',
            backgroundColor: `${selected ? 'success.main' : 'background.paper'}`,
            color: `${selected ? 'info.dark' : 'text.main'}`,
            // &::after is the arrow to the left of each sub tab
            '&::after': {
              content: '""',
              position: 'absolute',
              left: -5,
              top: '51%',
              width: 8,
              height: 8,
              borderTop: `2px solid ${theme.palette.success.main}`,
              borderRight: `2px solid ${theme.palette.success.main}`,
              borderWidth: '2px',
              transform: 'translate(-50%, -50%) rotate(45deg)',
            },
          }}
        >
          <Stack
            height={'100%'}
            direction={'column'}
            justifyContent={'center'}
            spacing={-1}
            fontFamily={'subtitle1.fontFamily'}
          >
            {/** TAB TITLE */}
            <Typography
              textAlign={'center'}
              variant={'inherit'}
              fontWeight={600}
              fontSize={{ sm: 10, md: 12, lg: 14, xl: 18, xxl: 24 }}
              letterSpacing={0}
            >
              {title}
            </Typography>
            <Stack
              direction={'row'}
              justifyContent={'center'}
              color={selected ? 'info.dark' : 'info.main'}
              fontWeight={600}
              fontSize={{ sm: 6, md: 8, lg: 10, xl: 10, xxl: 16 }}
              letterSpacing={0}
            >
              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={{ sm: 0, md: 1, lg: 2, xl: 4 }}
              >
                {/** ENERGY DEMAND / ENERGY DEMAND ICON */}
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  spacing={{ sm: 0, lg: 1, xl: 2 }}
                >
                  {tab === 'conventionalAlternative' ? (
                    <React.Fragment>
                      <Bolt />
                      <PropaneTank />
                    </React.Fragment>
                  ) : (
                    <Bolt />
                  )}
                  <Typography variant={'inherit'}>
                    {energyDemand === undefined
                      ? '--'
                      : `${formattedDecimal(energyDemand)} MWH`}
                  </Typography>
                </Stack>

                {/** IF EXISTS - COST COMPARISON, COST COMPARISON ICON */}
                {energyComparedToConventional && energyCompareIcon && (
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    spacing={{ sm: 0, lg: 1, xl: 2 }}
                  >
                    {energyCompareIcon}
                    <Typography variant={'inherit'}>
                      {energyComparedToConventional}
                    </Typography>
                  </Stack>
                )}

                {/** COST / COST ICON */}
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  spacing={{ sm: 0, lg: 1, xl: 2 }}
                >
                  <Paid />
                  <Typography variant={'inherit'}>
                    {cost === undefined ? '--' : formattedUSD(cost)}
                  </Typography>
                </Stack>

                {/** IF EXISTS - COST COMPARISON, COST COMPARISON ICON */}
                {costComparedToConventional && costCompareIcon && (
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    spacing={{ sm: 0, lg: 1, xl: 2 }}
                  >
                    {costCompareIcon}
                    <Typography variant={'inherit'}>
                      {costComparedToConventional}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  },
);

BaseDecarbTab.displayName = 'BaseDecarbTab';
